import { defineStore } from 'pinia'

import {
  Morsmal,
  OtEnhet,
  OtStatus,
  OtAktivitet,
  OtGruppe,
  Saksbehandler,
  Skolear,
  AktivBrukerMedRettigheter,
  Fylke,
  BrukerRettighet,
  Skole
} from '@/modules/core/models/types'
import { kodeverkClient, otBrukerClient } from '@/openapi/clients'
import { computed, ref } from 'vue'
import { cached, cachedRefetchEmptyArray } from '@/helpers/cache'
import { groupBy } from '@/helpers'
import { setGlobalLocale } from '@/i18n'

export const useCoreStore = defineStore('coreStore', () => {
  const aktivtSkolear = ref<Skolear>({} as Skolear)
  const currentSkolear = ref<Skolear>({} as Skolear)
  const aktivBrukerMedRettigheter = ref<AktivBrukerMedRettigheter>()

  const viewportHeightWithoutHeaderAndFooter = ref<number>()

  const brukerId = ref(<string>'')
  const saksbehandlerId = ref(<string>'')

  const mineEnheter = ref(<OtEnhet[]>[])
  const lasterMineEnheter = ref(<boolean>false)
  const mineSaksbehandlere = ref(<Saksbehandler[]>[])
  const lasterMineSaksbehandlere = ref(<boolean>false)

  const otEnheter = ref(<OtEnhet[]>[])
  const otStatuser = ref(<OtStatus[]>[])
  const otAktiviteter = ref(<OtAktivitet[]>[])
  const otGrupper = ref(<OtGruppe[]>[])
  const saksbehandlere = ref(<Saksbehandler[]>[])
  const morsmal = ref(<Morsmal[]>[])
  const skoler = ref(<Skole[]>[])
  const skolear = computed(() => {
    const valgbare = [] as Skolear[]
    if (currentSkolear.value) {
      const kode = currentSkolear.value.kode
      valgbare.push(createSkolear(kode, -1))
      valgbare.push(createSkolear(kode, 0))
      valgbare.push(createSkolear(kode, 1))
      return valgbare
    }
    return valgbare
  })

  const lasterRegioner = ref(<boolean>false)
  const lasterOtEnheter = ref(<boolean>false)
  const lasterOtStatuser = ref(<boolean>false)
  const lasterOtAktiviteter = ref(<boolean>false)
  const lasterOtGrupper = ref(<boolean>false)
  const lasterSaksbehandlere = ref(<boolean>false)
  const lasterMorsmal = ref(<boolean>false)
  const lasterSkolear = ref(<boolean>false)
  const lasterSkoler = ref(<boolean>false)

  const fylke = ref<Fylke | undefined>()
  const idToken = ref<any>()

  const harFylkesAdminRettighet = computed(() => {
    if (Array.isArray(idToken?.value?.permissions)) {
      const permissions = Array.from(idToken.value.permissions) as string[]
      const fylkesAdminRettighet = permissions?.find((perm: string) =>
        perm.startsWith('vigo:ot:fylke')
      )
      return !!fylkesAdminRettighet
    }
    return false
  })

  const bydelAdminRettighetForNumre = computed<string[]>(() => {
    if (Array.isArray(idToken?.value?.permissions)) {
      const permissions = Array.from(idToken.value.permissions) as string[]
      return permissions
        .filter((perm) => perm.startsWith('vigo:ot:bydel:'))
        .map((perm) => perm.split(':').slice(-1)[0]!)
    }
    return []
  })

  let rettigheterPerBrukerid = new Map<string, BrukerRettighet[]>()

  async function doHentAktivBrukerMedRettigheter() {
    lasterMineSaksbehandlere.value = true
    lasterMineEnheter.value = true
    lasterOtEnheter.value = true
    const { data } = await otBrukerClient.GET('/v1/aktiv-bruker-med-rettigheter')
    if (data && data.fylke) {
      aktivBrukerMedRettigheter.value = data
      brukerId.value = aktivBrukerMedRettigheter.value.bruker.id
      saksbehandlerId.value = aktivBrukerMedRettigheter.value.bruker.saksbehandlerId
      const enhetsIds =
        aktivBrukerMedRettigheter.value?.mineRettigheter.map((rettighet) => rettighet.enhetId) ?? []
      mineEnheter.value = aktivBrukerMedRettigheter.value?.enheter.filter((enhet) =>
        enhetsIds.some((enhetsId) => enhetsId === enhet.id)
      )
      mineSaksbehandlere.value = aktivBrukerMedRettigheter.value?.saksbehandlere

      const rettigheter = aktivBrukerMedRettigheter.value.alleRettighter
      const enhetRettigheter = rettigheter.filter((rettighet) => rettighet.rettighet !== 'INGEN')

      rettigheterPerBrukerid = groupBy(enhetRettigheter, (rettighet) => rettighet.brukerId)

      otEnheter.value = aktivBrukerMedRettigheter.value.enheter
    }
    lasterMineSaksbehandlere.value = false
    lasterMineEnheter.value = false
    lasterOtEnheter.value = false
  }

  function saksbehandlereForEnhetIds(enhetIds: string[], tilgangTilAlleEnheter: boolean) {
    if (
      !aktivBrukerMedRettigheter.value?.alleRettighter ||
      !aktivBrukerMedRettigheter.value?.saksbehandlere
    ) {
      return []
    }

    const saksbehandlerIds: string[] = []
    if (enhetIds.length > 0) {
      rettigheterPerBrukerid.forEach((value, key) => {
        if (
          tilgangTilAlleEnheter &&
          enhetIds.every((enhetId) => value.some((rettighet) => rettighet.enhetId === enhetId))
        ) {
          saksbehandlerIds.push(key)
        } else if (
          !tilgangTilAlleEnheter &&
          value.some((rettighet) => enhetIds.includes(rettighet.enhetId))
        ) {
          saksbehandlerIds.push(key)
        }
      })
    }

    return aktivBrukerMedRettigheter.value?.saksbehandlere.filter((behandler) =>
      saksbehandlerIds.includes(behandler.id)
    )
  }

  function saksbehandlereAndreEnheter(enhetId?: string, inkluderIder?: string[]) {
    if (
      !aktivBrukerMedRettigheter.value?.alleRettighter ||
      !aktivBrukerMedRettigheter.value?.saksbehandlere ||
      !enhetId
    ) {
      return []
    }

    const saksbehandlerIds: string[] = []
    rettigheterPerBrukerid.forEach((value, key) => {
      if (!value.some((rettighet) => rettighet.enhetId === enhetId)) {
        saksbehandlerIds.push(key)
      }
    })

    return aktivBrukerMedRettigheter.value?.saksbehandlere.filter(
      (behandler) =>
        saksbehandlerIds.includes(behandler.id) ||
        (inkluderIder && inkluderIder.includes(behandler.id))
    )
  }

  async function hentAktivBrukerMedRettigheter() {
    return cached(aktivBrukerMedRettigheter, () => doHentAktivBrukerMedRettigheter())
  }

  async function doHentOtAktiviteter() {
    lasterOtAktiviteter.value = true
    try {
      const { data, error } = await kodeverkClient.GET('/v1/ot-aktivitet')
      if (error) {
        Promise.reject(error)
        return
      }
      otAktiviteter.value = data.aktiviteter
    } finally {
      lasterOtAktiviteter.value = false
    }
  }

  async function hentOtAktiviteter() {
    return cachedRefetchEmptyArray(otAktiviteter, () => doHentOtAktiviteter())
  }

  async function doHentOtGrupper() {
    lasterOtGrupper.value = true
    try {
      const { data, error } = await kodeverkClient.GET('/v1/ot-gruppe')
      if (error) {
        Promise.reject(error)
        return
      }
      otGrupper.value = data.grupper
    } finally {
      lasterOtGrupper.value = false
    }
  }

  async function hentOtGrupper() {
    return cachedRefetchEmptyArray(otGrupper, () => doHentOtGrupper())
  }

  async function doHentMorsmal() {
    lasterMorsmal.value = true
    try {
      const { data, error } = await kodeverkClient.GET('/v1/morsmal')
      if (error) {
        Promise.reject(error)
        return
      }
      morsmal.value = data.morsmal
    } finally {
      lasterMorsmal.value = false
    }
  }

  async function hentMorsmal() {
    return cachedRefetchEmptyArray(morsmal, () => doHentMorsmal())
  }

  async function doHentSkoler() {
    lasterSkoler.value = true
    try {
      const response = await kodeverkClient.GET('/v1/skole')
      if (response.data?.skoler) {
        skoler.value = response.data.skoler
      } else {
        skoler.value = []
      }
    } catch (error) {
      console.error('Error fetching schools:', error)
      skoler.value = []
    } finally {
      lasterSkoler.value = false
    }
  }

  async function hentSkoler() {
    return cachedRefetchEmptyArray(skoler, () => doHentSkoler())
  }

  async function doHentOtStatuser() {
    lasterOtStatuser.value = true

    try {
      const { data, error } = await kodeverkClient.GET('/v1/otstatus')
      if (error) {
        Promise.reject(error)
        return
      }
      otStatuser.value = data.otStatus
    } finally {
      lasterOtStatuser.value = false
    }
  }

  async function hentOtStatuser() {
    return cachedRefetchEmptyArray(otStatuser, () => doHentOtStatuser())
  }

  function createSkolear(kode: string, delta: number): Skolear {
    const year = Number.parseInt(kode) + delta * 10001
    const stringYear = year.toString()
    return {
      id: stringYear,
      kode: stringYear,
      navn: `${stringYear.substring(0, 4)}/${stringYear.substring(4)}`
    }
  }

  function updateFromIdToken(token: any) {
    const changed = token && token.sid && (!idToken.value || token.sid !== idToken.value?.sid)

    idToken.value = token
    if (changed) {
      fylke.value = {
        navn: token.county_name,
        fylkesnr: token.county_number,
        id: token.county_id
      }

      const year = createSkolear(token.current_schoolyear, 0)
      aktivtSkolear.value = year
      currentSkolear.value = year
      if (token.locale === 'nn-NO') {
        setGlobalLocale('nn')
      }
      if (token.locale === 'nb-NO') {
        setGlobalLocale('nb')
      }
    }
  }

  function isDisabledForPastSchoolYear() {
    return skolear.value[0]?.kode === aktivtSkolear.value.kode
  }

  return {
    brukerId,
    saksbehandlerId,
    aktivBrukerMedRettigheter,
    aktivtSkolear,
    mineEnheter,
    mineSaksbehandlere,
    otEnheter,
    otStatuser,
    morsmal,
    skolear,
    skoler,
    fylke,
    harFylkesAdminRettighet,
    bydelAdminRettighetForNumre,
    otAktiviteter,
    otGrupper,
    saksbehandlere,
    lasterRegioner,
    lasterOtEnheter,
    lasterOtStatuser,
    lasterMineEnheter,
    lasterMineSaksbehandlere,
    lasterMorsmal,
    lasterSkolear,
    lasterSkoler,
    lasterOtAktiviteter,
    lasterOtGrupper,
    lasterSaksbehandlere,
    hentAktivBrukerMedRettigheter,
    hentOtStatuser,
    hentMorsmal,
    hentOtAktiviteter,
    hentOtGrupper,
    hentSkoler,
    updateFromIdToken,
    saksbehandlereForEnhetIds,
    saksbehandlereAndreEnheter,
    isDisabledForPastSchoolYear,
    viewportHeightWithoutHeaderAndFooter
  }
})
